import { useState } from 'react';

/**
 * The emoji tray in the composer.
 *
 * Hand-rolled rather than pulled from a package: the picker libraries ship a
 * full unicode dataset plus their own theming, which is a lot of weight and a
 * lot of rounded-corner opinion for a keyboard that already offers every emoji
 * there is. This covers the ones people reach for in a chat, in the app's own
 * type, and costs nothing to load.
 *
 * Recents are kept per-device, because the second time you send 🔥 should be
 * faster than the first.
 */
const RECENT_KEY = 'pr.emoji.recent';
const MAX_RECENT = 24;

const GROUPS = [
  {
    key: 'faces',
    label: 'Faces',
    emoji: ['😀', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎', '🤩', '🥳', '😅', '😉', '🙃', '😇', '🤔', '🤨', '😐', '🙄', '😴', '🤤', '😭', '😤', '😡', '🤯', '😱', '🥺', '😳', '🤫', '🤭', '😬', '🙈', '💀'],
  },
  {
    key: 'people',
    label: 'People',
    emoji: ['👋', '🙌', '👏', '🤝', '👍', '👎', '🤞', '✌️', '🤟', '🫶', '💪', '🙏', '👀', '🕺', '💃', '🧍', '🚶', '🏃', '🤷', '🤦', '💅', '🫂'],
  },
  {
    key: 'party',
    label: 'Party',
    emoji: ['🎉', '🎊', '🪩', '🍾', '🥂', '🍻', '🍺', '🍷', '🍸', '🍹', '🎂', '🍕', '🌮', '🍔', '🍟', '☕', '🎶', '🎵', '🎧', '🎤', '🎸', '🥁', '🎹', '🔥'],
  },
  {
    key: 'things',
    label: 'Things',
    emoji: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💯', '✨', '⭐', '🌟', '⚡', '💥', '🌈', '☀️', '🌙', '❄️', '🌊', '🏔️', '🚗', '✈️', '🎟️', '📸', '💸', '🎁', '🏆', '✅', '❌', '❓', '❗', '👑'],
  },
];

function readRecent() {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(v) ? v.slice(0, MAX_RECENT) : [];
  } catch { return []; }
}

export function rememberEmoji(emoji) {
  try {
    const next = [emoji, ...readRecent().filter((e) => e !== emoji)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* storage off — the tray just won't remember */ }
}

export default function EmojiPicker({ onPick, onClose }) {
  const [recent] = useState(readRecent);
  const [tab, setTab] = useState(recent.length ? 'recent' : 'faces');

  const groups = recent.length
    ? [{ key: 'recent', label: 'Recent', emoji: recent }, ...GROUPS]
    : GROUPS;
  const active = groups.find((g) => g.key === tab) || groups[0];

  return (
    <div className="border-t-2 border-black bg-white">
      <div className="flex items-stretch border-b-2 border-black">
        <div className="flex min-w-0 flex-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setTab(g.key)}
              className={`flex-shrink-0 border-r-2 border-black px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest ${
                g.key === active.key ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-50'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close emoji"
          className="flex-shrink-0 border-l-2 border-black px-3 font-mono text-[12px] font-bold hover:bg-black hover:text-white"
        >
          ×
        </button>
      </div>
      <div className="grid max-h-[34vh] grid-cols-8 gap-0.5 overflow-y-auto overscroll-contain p-2 sm:grid-cols-10">
        {active.emoji.map((e, i) => (
          <button
            key={`${e}-${i}`}
            type="button"
            // The tray stays open: picking emoji is usually picking several.
            onClick={() => { rememberEmoji(e); onPick(e); }}
            className="flex h-9 items-center justify-center text-[20px] leading-none hover:bg-gray-100"
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
